import * as React from 'react';
import type { StageErrorSummary } from '@chess/api-insights';
import { FilterAnchor, StatTile, TextSkeleton, type CardProps, type FilterLink } from '@chess/api-insights/charts';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import './middlegame-blunders.css';

// The middlegame blunders card: the reader's moves in the middlegame the
// review judges blunders over the last 30 days, and the way to those
// games in the library, where each blunder waits in the middlegame's moments
// table with its puzzle. Drawn from props only; the container reads the
// game store. The card's body: the section draws it in the dashboard's
// frame (`MIDDLEGAME_BLUNDERS_CARD`, `registerCard`).

export type MiddlegameBlundersCardProps = CardProps<{ middlegame: StageErrorSummary }, {
  /** Opens the library filtered to games with a middlegame blunder. */
  link?: FilterLink;
}>;

/** The frame's title: the registration's and the stories'. */
export const MIDDLEGAME_BLUNDERS_CARD = { title: 'Middlegame blunders' };
const ASIDE = 'moves judged blunders';

export const MiddlegameBlundersCard: React.FC<MiddlegameBlundersCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <StatTile loading unit="blunders" />
        <p className="middlegame-card-ratio">
          <TextSkeleton sample="In 3 of 7 games · 0.6 per game" />
        </p>
        <CardNote>
          <TextSkeleton sample="7 scanned games with a middlegame" />
        </CardNote>
      </>
    );
  const { middlegame, link } = props;
  const current = middlegame;
  const note = current.scanned ? `${current.scanned} scanned ${current.scanned === 1 ? 'game' : 'games'} with a middlegame` : undefined;
  let body: React.ReactNode;
  if (!current.scanned) body = <CardEmpty>Scan games to find your middlegame blunders.</CardEmpty>;
  else if (!current.blunders) body = <CardEmpty>No middlegame blunders in the last 30 days.</CardEmpty>;
  else
    body = (
      <>
        <StatTile value={current.blunders} unit={current.blunders === 1 ? 'blunder' : 'blunders'} />
        <p className="middlegame-card-ratio">
          In {current.blunderGames} of {current.scanned} {current.scanned === 1 ? 'game' : 'games'} · {(current.blunders / current.scanned).toFixed(1)} per game
        </p>
        <FilterAnchor className="middlegame-card-link" link={link} filter={{ blunder: 'middlegame' }} title="Every game with a middlegame blunder, of the last 30 days">
          Games in the library →
        </FilterAnchor>
      </>
    );
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      {body}
      <CardNote>{note}</CardNote>
    </>
  );
};
